import {
  Box,
  Button,
  Form,
  FormField,
  Heading,
  Layer,
  ResponsiveContext,
  Select,
  Text,
  TextInput,
  Tip
} from 'grommet'
import React, { useContext, useState } from 'react'
import ErrorProvider from './ErrorProvider'
import GlobalConfig from '../GlobalConfig'
import { Constants, I18n } from 'galarm-config'
import { stringNotEmpty } from '../utils/validations'
import { useSelector } from 'react-redux'
import UserContext from './UserContext'
import FormFieldWithAllBorders from './FormFieldWithAllBorders'
import ReactSelect from 'react-select'
import { CircleInformation, Subtract } from 'grommet-icons'
import { DateTimeUtils, StringUtils } from 'galarm-shared'
import { isEmpty } from 'lodash'
import LabelManager from './LabelManager'
import { ChooseDurationContainer, SecondaryText } from 'web-components'
import { collection, doc, setDoc } from 'firebase/firestore'
import { v4 as uuidv4 } from 'uuid'
import { logEvent } from 'firebase/analytics'

// const severityOptions = [
//   { label: I18n.t('low'), value: 'low' },
//   { label: I18n.t('medium'), value: 'medium' },
//   { label: I18n.t('high'), value: 'high' }
// ]

const EditAlert = ({ isEdit, alert, onClose, displayApiCodeDialog }) => {
  const { user } = useContext(UserContext)
  const size = useContext(ResponsiveContext)

  const enterpriseAccountId = window.localStorage.getItem('enterpriseAccountId')

  const integrationConfigs = useSelector(
    state => state.accountInfo.integrationConfigs
  )

  const escalationIntervals = useSelector(
    state => state.accountInfo.escalationIntervals
  )

  const existingIncidentPrefixes = useSelector(
    state => state.accountInfo.incidentPrefixes
  )

  const availableLabels = useSelector(state => state.accountInfo.labels)

  const alertId = isEdit
    ? alert.id
    : doc(
        collection(
          GlobalConfig.firestoreDb,
          'enterpriseAccounts',
          enterpriseAccountId,
          'alerts'
        )
      ).id

  const INITIAL_STATE = isEdit
    ? {
        id: alertId,
        name: alert.name,
        severity: alert.severity,
        triggers: alert.triggers,
        integrations: alert.integrations.map(id =>
          integrationConfigs.find(config => config.id === id)
        ),
        preferredSourceForIncidents: alert.preferredSourceForIncidents || '',
        incidentPrefix: alert.incidentPrefix || '',
        incidentNameTemplate:
          alert.incidentNameTemplate || '${identifier} - ${name}',
        autoCloseIncidents: alert.autoCloseIncidents ?? false,
        autoCloseIncidentsDuration: alert.autoCloseIncidentsDuration ?? -1,
        labels: alert.labels || []
      }
    : {
        id: alertId,
        name: '',
        severity: 'high',
        triggers: [
          {
            id: doc(
              collection(
                GlobalConfig.firestoreDb,
                'enterpriseAccounts',
                enterpriseAccountId,
                'alerts',
                alertId,
                'triggers'
              )
            ).id,
            delay: 0,
            assignee: {}
          }
        ],
        integrations: [],
        preferredSourceForIncidents: '',
        incidentPrefix: '',
        incidentNameTemplate: '${identifier} - ${name}',
        autoCloseIncidents: GlobalConfig.defaultAutoCloseIncidents,
        autoCloseIncidentsDuration:
          GlobalConfig.defaultAutoCloseIncidentsDuration,
        labels: []
      }

  const [value, setValue] = useState(INITIAL_STATE)

  const [errors, setErrors] = useState([])
  const addError = error => setErrors(errors.concat([error]))
  const onClearError = errorToClear => {
    const newErrors = errors.filter(error => error !== errorToClear)
    setErrors(newErrors)
  }

  const [showChooseDelayDialog, setShowChooseDelayDialog] = useState({})
  const displayChooseDelayDialog = triggerIndex =>
    setShowChooseDelayDialog({ triggerIndex })
  const hideChooseDelayDialog = () => setShowChooseDelayDialog({})

  const [showLabelManager, setShowLabelManager] = useState(false)
  const displayLabelManager = () => setShowLabelManager(true)
  const hideLabelManager = () => setShowLabelManager(false)

  const members = useSelector(state => state.accountInfo.members)
  const membersWithLicense = members.filter(
    member =>
      member.subscriptionKey &&
      member.role !== Constants.ENTERPRISE_USER_ROLES.INCIDENT_COORDINATOR
  )
  const memberOptions = membersWithLicense.map(member => ({
    label: StringUtils.createDisplayName(member.firstName, member.lastName),
    value: member.id,
    assigneeType: Constants.ASSIGNEE_TYPES.MEMBER
  }))

  const teams = useSelector(state => state.accountInfo.teams)
  const teamOptions = teams.map(team => ({
    label: team.name,
    value: team.id,
    assigneeType: Constants.ASSIGNEE_TYPES.TEAM
  }))

  const rotations = useSelector(state => state.accountInfo.rotations)
  const rotationOptions = rotations.map(rotation => ({
    label: rotation.name,
    value: rotation.id,
    assigneeType: Constants.ASSIGNEE_TYPES.ROTATION
  }))

  const assigneeOptions = [
    {
      label: I18n.t('members'),
      options: memberOptions
    },
    {
      label: I18n.t('teams'),
      options: teamOptions
    },
    {
      label: I18n.t('rotations'),
      options: rotationOptions
    }
  ]

  const delayOptions = [
    {
      label: I18n.t('atTimeOfEvent'),
      value: 0
    },
    {
      label: I18n.t('afterNMinutes', { count: 30 }),
      value: 30 * Constants.MSEC_IN_MINUTE
    },
    {
      label: I18n.t('afterNHours', { count: 1 }),
      value: Constants.MSEC_IN_HOUR
    },
    {
      label: I18n.t('afterNHours', { count: 4 }),
      value: 4 * Constants.MSEC_IN_HOUR
    }
  ]

  const preferredSourceForIncidentsOptions = [
    {
      label: 'None',
      value: ''
    },
    {
      label: 'Curl No Data Basic Auth',
      value: Constants.SOURCES_FOR_INCIDENTS.CURL_NO_DATA_BASIC_AUTH
    },
    {
      label: 'Curl No Data Bearer Token',
      value: Constants.SOURCES_FOR_INCIDENTS.CURL_NO_DATA_BEARER_TOKEN
    },
    {
      label: 'Curl With Data Basic Auth',
      value: Constants.SOURCES_FOR_INCIDENTS.CURL_WITH_DATA_BASIC_AUTH
    },
    {
      label: 'Curl With Data Bearer Token',
      value: Constants.SOURCES_FOR_INCIDENTS.CURL_WITH_DATA_BEARER_TOKEN
    },
    {
      label: 'Datadog',
      value: Constants.SOURCES_FOR_INCIDENTS.DATA_DOG
    },
    {
      label: 'AWS SNS',
      value: Constants.SOURCES_FOR_INCIDENTS.AWS_SNS
    }
  ]

  const autoCloseIncidentsDurationOptions = [
    {
      label: I18n.t('never'),
      value: -1
    },
    { label: I18n.t('numDays', { count: 1 }), value: Constants.MSEC_IN_DAY },
    {
      label: I18n.t('numDays', { count: 2 }),
      value: 2 * Constants.MSEC_IN_DAY
    },
    {
      label: I18n.t('numDays', { count: 3 }),
      value: 3 * Constants.MSEC_IN_DAY
    },
    {
      label: I18n.t('numDays', { count: 4 }),
      value: 4 * Constants.MSEC_IN_DAY
    },
    {
      label: I18n.t('numDays', { count: 5 }),
      value: 5 * Constants.MSEC_IN_DAY
    },
    {
      label: I18n.t('numDays', { count: 6 }),
      value: 6 * Constants.MSEC_IN_DAY
    },
    {
      label: I18n.t('numDays', { count: 7 }),
      value: 7 * Constants.MSEC_IN_DAY
    },
    {
      label: I18n.t('numDays', { count: 10 }),
      value: 10 * Constants.MSEC_IN_DAY
    },
    {
      label: I18n.t('numDays', { count: 14 }),
      value: 14 * Constants.MSEC_IN_DAY
    }
  ]

  value.triggers.forEach(trigger => {
    if (
      trigger.delay &&
      delayOptions.findIndex(
        delayOption => delayOption.value === trigger.delay
      ) === -1
    ) {
      delayOptions.push({
        label: I18n.t('afterDuration', {
          durationString: DateTimeUtils.getDurationAsString(
            trigger.delay,
            '',
            true
          )
        }),
        value: trigger.delay
      })
    }
  })

  const sortedDelayOptions = delayOptions.sort(
    (option1, option2) => option1.value - option2.value
  )

  sortedDelayOptions.push({
    label: I18n.t('specify'),
    value: ''
  })

  const onSaveDelayForTrigger = (selectedOption, index) => {
    if (selectedOption === '') {
      displayChooseDelayDialog(index)
    } else {
      const newTriggers = value.triggers.map((trigger, i) =>
        i === index ? { ...trigger, delay: selectedOption } : trigger
      )
      setValue({
        ...value,
        triggers: newTriggers
      })
    }
  }

  const onSaveAssigneeForTrigger = (selectedOption, index) => {
    const assignee = {
      type: selectedOption.assigneeType,
      id: selectedOption.value,
      name: selectedOption.label
    }
    const newTriggers = value.triggers.map((trigger, i) =>
      i === index ? { ...trigger, assignee } : trigger
    )
    setValue({
      ...value,
      triggers: newTriggers
    })
  }

  const addTrigger = () => {
    const newTriggers = value.triggers.slice()
    newTriggers.push({
      id: uuidv4(),
      delay: '',
      assignee: {}
    })
    setValue({
      ...value,
      triggers: newTriggers
    })
  }

  const onRemoveTrigger = index => {
    const newTriggers = value.triggers.slice()
    newTriggers.splice(index, 1)
    setValue({
      ...value,
      triggers: newTriggers
    })
  }

  const getDisplayTextForTriggerDelay = duration =>
    I18n.t('assigneeWillBeAlertedAfterDelay', {
      durationString: DateTimeUtils.getDurationAsString(duration, '', true)
    })

  const onEditAlert = async ({ value }) => {
    try {
      GlobalConfig.showProgress({
        state: Constants.ProgressStates.IN_PROGRESS,
        message: isEdit ? I18n.t('editingAlert') : I18n.t('addingAlert'),
        closeable: false
      })

      const {
        id,
        name,
        severity,
        triggers: allTriggers,
        integrations,
        preferredSourceForIncidents,
        incidentPrefix,
        incidentNameTemplate,
        autoCloseIncidents,
        autoCloseIncidentsDuration,
        labels
      } = value

      // remove triggers with empty assignee or delay
      const triggers = allTriggers.filter(
        trigger => !isEmpty(trigger.assignee) && trigger.delay !== ''
      )

      if (triggers.length === 0) {
        addError(I18n.t('listenerMustHaveTriggers'))
        GlobalConfig.hideProgress()
        return
      }

      const assignees = triggers.map(trigger => trigger.assignee.id)

      const newAlert = {
        id,
        name,
        severity,
        assignees,
        triggers,
        integrations,
        preferredSourceForIncidents,
        notificationOrder: Constants.NOTIFICATION_ORDERS.SIMULTANEOUS,
        resolutionCriteria: Constants.RESOLUTION_CRITERIA.ANY_ONE,
        owner: user.uid,
        incidentPrefix: incidentPrefix || '',
        incidentNameTemplate: incidentNameTemplate || '${identifier} - ${name}',
        autoCloseIncidents: autoCloseIncidents ?? false,
        autoCloseIncidentsDuration:
          autoCloseIncidentsDuration ??
          GlobalConfig.defaultAutoCloseIncidentsDuration,
        labels: labels || []
      }

      await setDoc(
        doc(
          GlobalConfig.firestoreDb,
          'enterpriseAccounts',
          enterpriseAccountId,
          'alerts',
          id
        ),
        newAlert
      )
      GlobalConfig.hideProgress()

      logEvent(
        GlobalConfig.analytics,
        isEdit
          ? Constants.UserAnalyticsEvents.EDIT_ALERT
          : Constants.UserAnalyticsEvents.ADD_ALERT,
        {}
      )

      onClose()
      !isEdit && preferredSourceForIncidents && displayApiCodeDialog(newAlert)
    } catch (error) {
      console.error(error)
      addError(error.message)
      GlobalConfig.hideProgress()
    }
  }

  const validateIncidentPrefix = incidentPrefix => {
    if (isEdit && alert.incidentPrefix) {
      return
    }

    if (existingIncidentPrefixes.includes(incidentPrefix)) {
      return I18n.t('incidentPrefixAlreadyExists')
    }
    if (incidentPrefix.length > 5) {
      return I18n.t('incidentPrefixLengthExceeded')
    }
  }

  const validateIncidentNameTemplate = incidentNameTemplate => {
    const allowedKeys = ['identifier', 'name', 'summary']
    const matches = incidentNameTemplate.match(/\${(.*?)}/g) || []
    const isTemplateValid = matches.every(m =>
      allowedKeys.includes(m.slice(2, -1))
    )
    if (!isTemplateValid) {
      return I18n.t('invalidIncidentNameTemplate')
    }
  }

  const convertEscalationIntervals = escalationIntervals => {
    if (
      !escalationIntervals ||
      !Array.isArray(escalationIntervals) ||
      escalationIntervals.length !== 6
    ) {
      return null
    }

    return [
      [escalationIntervals[0], escalationIntervals[3]],
      [escalationIntervals[1], escalationIntervals[4]],
      [escalationIntervals[2], escalationIntervals[5]]
    ]
  }

  const escalationIntervalOptions =
    convertEscalationIntervals(escalationIntervals)

  // const onIntegrationsChange = selectedIntegrations => {
  //   setValue({
  //     ...value,
  //     integrations: selectedIntegrations
  //   })
  // }

  const customStyles = {
    control: provided => ({
      ...provided,
      border: 'none',
      minWidth: '250px'
    }),
    menuPortal: provided => ({
      ...provided,
      zIndex: 9999
    })
  }

  console.log('EditAlert', value)

  return (
    <ErrorProvider
      errors={errors}
      getErrorMessage={error => error}
      onClearError={onClearError}>
      <Box align="center" flex={{ shrink: 0 }}>
        <Heading level={4}>
          {isEdit ? I18n.t('editAlert') : I18n.t('addAlert')}
        </Heading>
        <Box
          background="white"
          pad="small"
          round="small"
          margin="medium"
          width="large">
          <Form
            value={value}
            onChange={nextValue => setValue(nextValue)}
            validate="submit"
            onSubmit={alertData => {
              onEditAlert(alertData)
            }}>
            <FormField
              htmlFor="name"
              name="name"
              label={
                <SecondaryText weight={500}>{I18n.t('name')}</SecondaryText>
              }
              validate={stringNotEmpty.bind(null, I18n.t('name'))}>
              <TextInput
                id="name"
                name="name"
                placeholder={I18n.t('enterAlertName')}
              />
            </FormField>
            <FormField
              disabled={isEdit && !!alert.incidentPrefix}
              htmlFor="incidentPrefix"
              name="incidentPrefix"
              label={
                <SecondaryText weight={500}>
                  {I18n.t('incidentPrefix')}
                </SecondaryText>
              }
              validate={validateIncidentPrefix}>
              <TextInput
                id="incidentPrefix"
                name="incidentPrefix"
                placeholder={I18n.t('enterIncidentPrefix')}
                disabled={isEdit && !!alert.incidentPrefix}
              />
            </FormField>
            <FormField
              htmlFor="incidentNameTemplate"
              name="incidentNameTemplate"
              label={
                <Box direction="row" align="center" gap="xsmall">
                  <SecondaryText weight={500}>
                    {I18n.t('incidentNameTemplate')}
                  </SecondaryText>
                  <Tip
                    content={
                      <Box pad="small" width="large">
                        <Text>
                          Customize how incidents appear in the mobile app. You
                          can use three variables in the template:
                          <br />
                          <code>${'{identifier}'}</code> – the incident&apos;s
                          unique ID
                          <br />
                          <code>${'{name}'}</code> – the name of the listener
                          <br />
                          <code>${'{summary}'}</code> – a short summary of the
                          incident passed in the API call as the summary field
                          <br />
                          <br />
                          Examples:
                          <br />
                          1.{' '}
                          <code>
                            ${'{identifier}'} - ${'{name}'}
                          </code>{' '}
                          will show &quot;ID - Listener Name&quot;
                          <br />
                          2. <code>${'{summary}'}</code> will show the incident
                          summary
                          <br />
                          3.{' '}
                          <code>
                            ${'{name}'}: ${'{summary}'}
                          </code>{' '}
                          will show &quot;Listener Name: Summary&quot;
                        </Text>
                      </Box>
                    }>
                    <CircleInformation color="textColor" size="18px" />
                  </Tip>
                </Box>
              }
              validate={validateIncidentNameTemplate}>
              <TextInput
                id="incidentNameTemplate"
                name="incidentNameTemplate"
                placeholder={I18n.t('enterIncidentNameTemplate')}
              />
            </FormField>
            <FormFieldWithAllBorders
              htmlFor="preferredSourceForIncidents"
              name="preferredSourceForIncidents"
              label={
                <Box direction="row" align="center" gap="xsmall">
                  <SecondaryText weight={500}>
                    {I18n.t('preferredSourceForIncidents')}
                  </SecondaryText>
                  <Tip
                    content={
                      <Box pad="small" width="large">
                        <Text style={{ whiteSpace: 'pre-line' }}>
                          {I18n.t('preferredSourceForIncidentsDescription')}
                        </Text>
                      </Box>
                    }>
                    <CircleInformation color="textColor" size="18px" />
                  </Tip>
                </Box>
              }>
              <Select
                id="preferredSourceForIncidents"
                name="preferredSourceForIncidents"
                options={preferredSourceForIncidentsOptions}
                placeholder={I18n.t('selectPlaceholder')}
                labelKey="label"
                valueKey={{ key: 'value', reduce: true }}
                onChange={({ option }) => {
                  setValue({
                    ...value,
                    preferredSourceForIncidents: option.value
                  })
                }}
              />
            </FormFieldWithAllBorders>
            <FormFieldWithAllBorders
              htmlFor="labels"
              name="labels"
              label={
                <Box
                  direction="row"
                  align="center"
                  justify="between"
                  width="100%">
                  <SecondaryText weight={500}>{I18n.t('labels')}</SecondaryText>
                  <Button
                    plain
                    label={I18n.t('manageLabels')}
                    onClick={displayLabelManager}
                    color="primary"
                  />
                </Box>
              }>
              <ReactSelect
                id="labels"
                name="labels"
                styles={customStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                placeholder={I18n.t('selectLabels')}
                options={availableLabels.map(label => ({
                  label: label.name,
                  value: label.id,
                  color: label.color
                }))}
                isMulti
                getOptionLabel={option => option.label}
                onChange={selectedOptions => {
                  setValue({
                    ...value,
                    labels: selectedOptions
                      ? selectedOptions.map(opt => opt.value)
                      : []
                  })
                }}
                value={value.labels
                  .map(labelId => {
                    const label = availableLabels.find(l => l.id === labelId)
                    return label
                      ? {
                          label: label.name,
                          value: label.id,
                          color: label.color
                        }
                      : null
                  })
                  .filter(Boolean)}
                formatOptionLabel={option => (
                  <Box direction="row" align="center" gap="small">
                    <Box
                      width="16px"
                      height="16px"
                      round="small"
                      background={option.color}
                    />
                    <Text>{option.label}</Text>
                  </Box>
                )}
              />
            </FormFieldWithAllBorders>
            {/* <FormFieldWithAllBorders
              htmlFor="severity"
              name="severity"
              label={I18n.t('severity')}
              validate={stringNotEmpty.bind(null, I18n.t('severity'))}>
              <Select
                id="severity"
                name="severity"
                options={severityOptions}
                placeholder={I18n.t('selectPlaceholder')}
                labelKey="label"
                valueKey={{ key: 'value', reduce: true }}
                onChange={({ option }) => {
                  setValue({
                    ...value,
                    severity: option.value
                  })
                }}
              />
            </FormFieldWithAllBorders> */}
            {/* <FormFieldWithAllBorders
              htmlFor="integrations"
              name="integrations"
              label={I18n.t('integrations')}>
              <ReactSelect
                id="integrations"
                name="integrations"
                styles={customStyles}
                isMulti
                menuPortalTarget={document.body}
                menuPosition="fixed"
                options={integrationConfigs}
                getOptionLabel={integrationConfig => integrationConfig.name}
                onChange={onIntegrationsChange}
                value={value.integrations}
              />
            </FormFieldWithAllBorders> */}
            <FormFieldWithAllBorders
              htmlFor="autoCloseIncidents"
              name="autoCloseIncidents"
              label={
                <Box direction="row" align="center" gap="xsmall">
                  <SecondaryText weight={500}>
                    {I18n.t('autoCloseIncidents')}
                  </SecondaryText>
                  <Tip
                    content={
                      <Box pad="small" width="large">
                        <Text style={{ whiteSpace: 'pre-line' }}>
                          {I18n.t('autoCloseIncidentsDescription')}
                        </Text>
                      </Box>
                    }>
                    <CircleInformation color="textColor" size="18px" />
                  </Tip>
                </Box>
              }>
              <Select
                id="autoCloseIncidentsDuration"
                name="autoCloseIncidentsDuration"
                options={autoCloseIncidentsDurationOptions}
                labelKey="label"
                valueKey={{ key: 'value', reduce: true }}
                onChange={({ option }) => {
                  setValue({
                    ...value,
                    autoCloseIncidents: option.value !== -1,
                    autoCloseIncidentsDuration: option.value
                  })
                }}
              />
            </FormFieldWithAllBorders>
            {/* <FormFieldWithNoBorder
              border={false}
              htmlFor="autoCloseIncidents"
              name="autoCloseIncidents"
              label={
                <Box direction="row" align="center" gap="xsmall">
                  <SecondaryText weight={500}>
                    {I18n.t('autoCloseIncidents')}
                  </SecondaryText>
                  <Tip
                    content={
                      <Box pad="small" width="large">
                        <Text style={{ whiteSpace: 'pre-line' }}>
                          {I18n.t('autoCloseIncidentsDescription')}
                        </Text>
                      </Box>
                    }>
                    <CircleInformation color="textColor" size="18px" />
                  </Tip>
                </Box>
              }>
              <Box direction="row" align="center" gap="medium">
                <CheckBox
                  id="autoCloseIncidents"
                  name="autoCloseIncidents"
                  toggle={true}
                  onChange={() => {
                    setValue({
                      ...value,
                      autoCloseIncidents: !value.autoCloseIncidents
                    })
                  }}
                />
                <Select
                  id="autoCloseIncidentsDuration"
                  name="autoCloseIncidentsDuration"
                  disabled={!value.autoCloseIncidents}
                  options={autoCloseIncidentsDurationOptions}
                  labelKey="label"
                  valueKey={{ key: 'value', reduce: true }}
                  placeholder={I18n.t('selectPlaceholder')}
                  onChange={({ option }) => {
                    setValue({
                      ...value,
                      autoCloseIncidentsDuration: option.value
                    })
                  }}
                />
              </Box>
            </FormFieldWithNoBorder> */}
            <Box pad={{ top: 'xsmall' }}>
              <Box direction="row" align="center" gap="xsmall">
                <SecondaryText weight={500} margin={{ horizontal: 'small' }}>
                  {I18n.t('escalationPolicy')}
                </SecondaryText>
                <Tip content={I18n.t('escalationPolicyTooltip')}>
                  <CircleInformation color="textColor" size="18px" />
                </Tip>
              </Box>
              {value.triggers?.map((trigger, index) => {
                const delayId = `triggers[${index}].delay`
                const assigneeId = `triggers[${index}].assignee.id`

                let triggerAssigneeValue
                if (!isEmpty(trigger.assignee)) {
                  triggerAssigneeValue = {
                    label: trigger.assignee.name,
                    value: trigger.assignee.id,
                    assigneeType: trigger.assignee.type
                  }
                }

                return (
                  <Box
                    key={index}
                    direction={size === 'small' ? 'column' : 'row'}
                    gap="small"
                    align="center"
                    margin="small">
                    <Text>{I18n.t('triggerName', { index: index + 1 })}</Text>
                    <FormFieldWithAllBorders
                      htmlFor={delayId}
                      name={delayId}
                      margin="none"
                      // validate={stringNotEmpty.bind(null, I18n.t('delay'))}
                    >
                      <Select
                        id={delayId}
                        name={delayId}
                        options={
                          index === 0
                            ? sortedDelayOptions
                            : sortedDelayOptions.slice(1)
                        }
                        placeholder={I18n.t('selectPlaceholder')}
                        value={trigger.delay}
                        labelKey="label"
                        valueKey={{ key: 'value', reduce: true }}
                        onChange={({ option }) => {
                          onSaveDelayForTrigger(option.value, index)
                        }}
                      />
                    </FormFieldWithAllBorders>
                    <FormFieldWithAllBorders
                      htmlFor={assigneeId}
                      name={assigneeId}
                      margin="none"
                      // validate={stringNotEmpty.bind(null, I18n.t('assignee'))}
                    >
                      <ReactSelect
                        id={assigneeId}
                        name={assigneeId}
                        styles={customStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        placeholder={I18n.t('chooseAssigneePlaceholder')}
                        options={assigneeOptions}
                        getOptionLabel={assignee => assignee.label}
                        onChange={option =>
                          onSaveAssigneeForTrigger(option, index)
                        }
                        value={triggerAssigneeValue}
                      />
                    </FormFieldWithAllBorders>
                    <Button
                      primary
                      onClick={() => onRemoveTrigger(index)}
                      icon={<Subtract color="white" size="small" />}
                    />
                  </Box>
                )
              })}
              <Button
                plain
                margin="small"
                color="primary"
                alignSelf="start"
                onClick={addTrigger}
                label={I18n.t('addTrigger')}
              />
            </Box>
            <Box direction="row" gap="medium" justify="center" margin="medium">
              <Button
                type="submit"
                primary
                style={{ color: 'white' }}
                label={isEdit ? I18n.t('save') : I18n.t('add')}
              />
            </Box>
          </Form>
        </Box>
        {!isEmpty(showChooseDelayDialog) && (
          <Layer
            background="lightTint"
            onEsc={hideChooseDelayDialog}
            onClickOutside={hideChooseDelayDialog}>
            <Box margin="medium">
              <ChooseDurationContainer
                intervals={escalationIntervalOptions}
                considerSeconds={true}
                onSaveDuration={duration => {
                  onSaveDelayForTrigger(
                    duration,
                    showChooseDelayDialog.triggerIndex
                  )
                  hideChooseDelayDialog()
                }}
                getDisplayTextForDuration={duration =>
                  getDisplayTextForTriggerDelay(duration)
                }
                title={I18n.t('delayForTrigger')}
                onClose={hideChooseDelayDialog}
              />
            </Box>
          </Layer>
        )}
        {showLabelManager && (
          <Layer
            background="lightTint"
            onEsc={hideLabelManager}
            onClickOutside={hideLabelManager}
            margin="medium">
            <Box margin="medium">
              <LabelManager onClose={hideLabelManager} />
            </Box>
          </Layer>
        )}
      </Box>
    </ErrorProvider>
  )
}

export default EditAlert
